"use client";
import { Dialog, type SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import { ApiPaths } from "@shared/constants/ApiPaths";
import { useUserAccount } from "@shared/contexts/UserAccountContext";
import { signout } from "@shared/handler/CommonHandler";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  actions?: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  sx?: SxProps;
};

const handlePasswordChange = () => {
  window.location.href = ApiPaths.Auth.passwordChange;
};

export const AccountDialog = ({
  open,
  onClose,
  actions,
  maxWidth = "sm",
  sx,
}: Props) => {
  const userAccount = useUserAccount();
  const router = useRouter();

  return (
    <Dialog
      hideBackdrop
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.16)",
            bgcolor: "#F9FAFB",
            p: 0,
            minWidth: 306,
            maxWidth: 306,
            width: 306,
            maxHeight: 403,
            position: "relative",
            overflow: "visible", // 追加: 三角形を隠さない
          },
        },
      }}
      sx={sx}
    >
      {/* 左抜きの吹き出し（三角形） */}
      <Box
        sx={{
          position: "absolute",
          top: "80%", // アバターの中央付近に合わせる
          left: -16, // より左に長く
          width: 16,
          height: 16,
          zIndex: 1,
          pointerEvents: "none",
          display: open ? "block" : "none",
        }}
      >
        <Box
          sx={{
            width: 0,
            height: 0,
            borderTop: "8px solid transparent",
            borderBottom: "8px solid transparent",
            borderRight: "16px solid #F9FAFB", // 幅16px, 高さ16px
          }}
        />
      </Box>
      {/* アカウントアイコン */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mt: 4,
          mb: 1,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <img
            src="/images/icon/side-menu/account_icon.svg"
            alt="account icon"
            width={64}
            height={64}
            style={{ borderRadius: "50%" }}
          />
        </Box>
        {/* 名前・権限・メールアドレス */}
        <Box sx={{ mt: 1, mb: 0.5, px: 4 }}>
          <Box sx={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
            {userAccount.accountName}
          </Box>
          <Box sx={{ fontSize: 13, color: "#6B7280", mt: 1.2, mb: 1.2 }}>
            {userAccount.email}
          </Box>
        </Box>
      </Box>
      <Divider sx={{ mx: 0, borderColor: "#E5E7EB", borderBottomWidth: 2 }} />
      <DialogContent
        sx={{
          p: 2,
          color: "#222",
          bgcolor: "#fff",
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
          minHeight: 80,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {/* アカウント表示・パスワード変更・ログアウト */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Button
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              px: 2,
              py: 1,
              borderRadius: 2,
              transition: "background 0.2s",
              "&:hover": { bgcolor: "#F3F4F6" },
              justifyContent: "flex-start",
            }}
            onClick={() => {
              router.push("/manager/accounts/personal");
              onClose();
            }}
          >
            <img
              src="/images/icon/side-menu/account_menu_icon.svg"
              alt="アカウント"
              width={24}
              height={24}
            />
            <Box sx={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
              アカウント表示
            </Box>
          </Button>
          <Button
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              px: 2,
              py: 1,
              borderRadius: 2,
              transition: "background 0.2s",
              "&:hover": { bgcolor: "#F3F4F6" },
              justifyContent: "flex-start",
            }}
            onClick={handlePasswordChange}
          >
            <img
              src="/images/icon/side-menu/password_change_icon.svg"
              alt="パスワード変更"
              width={24}
              height={24}
            />
            <Box sx={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
              パスワード変更
            </Box>
          </Button>
          <Button
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              px: 2,
              py: 1,
              borderRadius: 2,
              transition: "background 0.2s",
              "&:hover": { bgcolor: "#F3F4F6" },
              justifyContent: "flex-start",
            }}
            onClick={signout}
          >
            <img
              src="/images/icon/side-menu/signout_icon.svg"
              alt="ログアウト"
              width={24}
              height={24}
            />
            <Box sx={{ fontWeight: 700, fontSize: 16, color: "#222" }}>
              ログアウト
            </Box>
          </Button>
        </Box>
      </DialogContent>
      {actions && (
        <DialogActions
          sx={{
            px: 4,
            pb: 3,
            pt: 0,
            justifyContent: "flex-end",
            gap: 2,
            bgcolor: "#fff",
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};
